import { useCallback, useEffect, useState } from 'react';
import {
  deleteFormFieldOption,
  fetchFormFieldOptions,
  upsertFormFieldOption,
  type FormFieldOption,
} from '../../lib/applicationFormSettings';

const FORM_SLUG = 'customer-feedback';
const FIELD_KEY = 'reason';

export function DemCffSettingsPanel({ canEdit = true }: { canEdit?: boolean }) {
  const [rows, setRows] = useState<FormFieldOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState({ value: '', label: '', sort_order: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchFormFieldOptions(FORM_SLUG);
      setRows(all.filter((r) => r.field_key === FIELD_KEY));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load CFF reason options.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addOption(e: React.FormEvent) {
    e.preventDefault();
    const value = draft.value.trim() || draft.label.trim();
    const label = draft.label.trim() || draft.value.trim();
    if (!value || !label) {
      setError('Label is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertFormFieldOption({
        form_slug: FORM_SLUG,
        field_key: FIELD_KEY,
        value,
        label,
        sort_order: draft.sort_order || rows.length + 1,
        active: true,
      });
      setDraft({ value: '', label: '', sort_order: 0 });
      await load();
      setMessage('Reason option added.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add failed.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: FormFieldOption) {
    setSaving(true);
    setError(null);
    try {
      await upsertFormFieldOption({
        id: row.id,
        form_slug: FORM_SLUG,
        field_key: row.field_key,
        value: row.value,
        label: row.label,
        sort_order: row.sort_order,
        active: !row.active,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setSaving(false);
    }
  }

  async function removeOption(row: FormFieldOption) {
    if (!window.confirm(`Delete reason "${row.label}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteFormFieldOption(row.id);
      await load();
      setMessage('Reason option removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="af-settings-loading">Loading CFF reason options…</p>;
  }

  return (
    <section className="card settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2 className="settings-panel__title">CFF reason options</h2>
          <p className="page-lead">
            Reasons shown on Customer Feedback Form. Users can search and select only — they
            cannot type a custom reason.
          </p>
        </div>
        <span className="af-settings-section__badge">{rows.length} options</span>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {message ? <p className="form-success">{message}</p> : null}

      {canEdit ? (
        <form className="af-settings-add-card af-settings-add-card--top" onSubmit={(e) => void addOption(e)}>
          <h3 className="af-settings-add-card__title">Add reason</h3>
          <div className="af-settings-form__grid af-settings-form__grid--3">
            <label className="af-settings-field">
              <span>Label (shown)</span>
              <input
                value={draft.label}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    label: e.target.value,
                    value: d.value || e.target.value,
                  }))
                }
                placeholder="e.g. Damaged on delivery"
              />
            </label>
            <label className="af-settings-field">
              <span>Value (stored)</span>
              <input
                value={draft.value}
                onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                placeholder="Defaults to label"
              />
            </label>
            <label className="af-settings-field">
              <span>Sort order</span>
              <input
                type="number"
                value={draft.sort_order || ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, sort_order: Number(e.target.value) || 0 }))
                }
              />
            </label>
          </div>
          <button type="submit" className="btn-primary btn-sm" disabled={saving}>
            Add reason
          </button>
        </form>
      ) : null}

      <div className="af-settings-table-wrap">
        <table className="af-settings-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Value</th>
              <th>Order</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="af-settings-table__empty">
                  No reasons yet. Add options above.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={row.active ? '' : 'af-settings-table__row--muted'}>
                  <td>{row.label}</td>
                  <td>
                    <code className="af-settings-code">{row.value}</code>
                  </td>
                  <td>{row.sort_order}</td>
                  <td>
                    <span
                      className={`af-settings-status${row.active ? ' af-settings-status--on' : ''}`}
                    >
                      {row.active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="af-settings-table__actions">
                    {canEdit ? (
                      <>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => void toggleActive(row)}
                        >
                          {row.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          disabled={saving}
                          onClick={() => void removeOption(row)}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
